// Copyright 2026 FlairCV Contributors
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type { ProfileFormData } from '../types/schemas';

const PROFILE_QUERY_KEY = ['profile'] as const;
const CREDITS_QUERY_KEY = ['credits'] as const;

/** Hook for fetching the current user's profile. */
export function useProfile() {
  return useQuery({
    queryKey: PROFILE_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/api/v1/profile');

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
  });
}

/** Hook for updating the current user's profile. */
export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ProfileFormData) => {
      const { data, error } = await apiClient.PUT('/api/v1/profile', {
        body: {
          name: params.name || undefined,
          phone: params.phone || undefined,
          address: params.address || undefined,
          professional_summary: params.professional_summary || undefined,
          linkedin_url: params.linkedin_url || undefined,
          github_url: params.github_url || undefined,
          portfolio_url: params.portfolio_url || undefined,
          output_language_mode: params.output_language_mode || undefined,
          custom_output_language: params.custom_output_language || undefined,
          photo_url: params.photo_url || undefined,
          photo_settings: params.photo_settings || undefined,
        },
      });

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PROFILE_QUERY_KEY, data);
    },
  });
}

/** Hook for AI-polishing the professional summary. */
export function usePolishProfileSummary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      summary?: string;
      include_experiences?: boolean;
    }) => {
      const { data, error } = await apiClient.POST(
        '/api/v1/profile/polish-summary',
        {
          body: {
            ...(params.summary ? { summary: params.summary } : {}),
            ...(params.include_experiences
              ? { include_experiences: true }
              : {}),
          },
        },
      );

      if (error) {
        throw new Error(error.error.message);
      }

      return data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CREDITS_QUERY_KEY });
    },
  });
}
